import omit from 'lodash.omit'
import queryString from 'query-string'
import React, { useEffect, useRef, useState } from 'react'

import { Instrument, ObjectHash } from '../../types/shared'
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader } from '../components'
import { ChartsOrQuotePageQueryParams, LayoutType } from '../constants/common'
import ChartLayout from '../models/chart_layout'
import Quote from '../models/quote'
import { getInstrument } from '../utils/chart'
import fetchApi, { getFormDataFromObject } from '../utils/fetch_api'
import { captureException } from '../utils/helpers'
import { decodeQueryString } from '../utils/query_string'

interface IIdea {
  ticker: string
  instrument: string
  period: string
  company?: string
  date: string
  note: string
  id: string
}

function getIdeaUrl({ ticker, instrument, period, id }: Pick<IIdea, 'ticker' | 'instrument' | 'period' | 'id'>) {
  const searchObject = { t: ticker, p: period, i: id }
  const isStock = instrument === Instrument.Stock
  const isQuotePage = /\/quote\b/.test(window.location.pathname)
  const isStockOnQute = isQuotePage && isStock
  const path = !isQuotePage || isStock ? window.location.pathname : '/charts.ashx'

  return `${path}?${queryString.stringify({ ...searchObject, ...(isStockOnQute ? { ta: '2' } : { l: '1h1v' }) })}`
}

function IdeasList() {
  const [hasError, setHasError] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [ideas, setIdeas] = useState<IIdea[]>([])
  const { i: currentIdea } = decodeQueryString<ChartsOrQuotePageQueryParams>()

  useEffect(() => {
    async function fetchIdeas() {
      window.gtag?.('event', 'load', { event_category: 'ideas' })
      try {
        const data = await fetchApi({ location: '/api/ideas_load.ashx', throwOnStatusCodes: [404] })
        const ideas = data.ideas.map((idea: IIdea) => ({
          ...idea,
          instrument: getInstrument(idea.ticker),
        }))

        setIdeas(ideas)
        setHasFetched(true)
      } catch {
        setHasError(true)
      }
    }

    fetchIdeas()
  }, [])

  const onDeleteIdeaClick = useRef(async (idea: IIdea) => {
    if (!window.confirm('Do you really want to delete this idea?')) return

    const body = [
      ['id', idea.id],
      ['ticker', idea.ticker],
      ['queryString', document.location.search],
      ['locationPathname', document.location.pathname],
      ['currentIdea', currentIdea],
    ]
      .map((row) => row.join('='))
      .join('&')

    setIsDeleting(true)

    const response = await fetch('/api/idea_delete.ashx', {
      body,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      method: 'post',
    })

    if (currentIdea === String(idea.id)) {
      window.location.href = response.url
      return
    }

    // Check if the page was loaded with the idea which is going to be deleted here
    // If that's the case, refresh the page to remove idea data from the page so when user navigates back to idea page it shows idea 404
    const configIdeaId = window.FinvizChartsSettings?.ideaChart?.idea?.id ?? window.globalChartConfig?.idea?.id
    if (configIdeaId === idea.id) {
      window.location.reload()
      return
    }

    setIdeas((ideas) => ideas.filter((stateIdea) => stateIdea.id !== idea.id))
    setIsDeleting(false)
  })

  if (!hasFetched || hasError) {
    return (
      <div className="flex h-40 items-center justify-center text-sm font-bold">
        {hasError ? 'Unexpected error occured, please refresh the page.' : 'Loading…'}
      </div>
    )
  }

  if (ideas.length === 0) {
    return (
      <div
        data-testid="charts-notice-ideas-no-ideas"
        className="flex h-40 items-center justify-center text-sm font-bold"
      >
        No saved ideas.
      </div>
    )
  }

  return (
    <>
      {ideas.map((idea) => (
        <div
          key={idea.id}
          className="-mx-2 flex cursor-pointer items-center justify-between rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={() => (document.location = getIdeaUrl(idea))}
        >
          <div className="flex flex-col overflow-hidden">
            <h1 className="m-0 font-bold">
              {idea.ticker}
              {idea.company && ' - ' + idea.company}
            </h1>
            <span className="text-sm" data-testid="charts-notice-idea-date">
              {idea.date}
            </span>
            {idea.note !== '' ? (
              <p className="overflow-hidden text-ellipsis whitespace-nowrap text-xs">{idea.note}</p>
            ) : null}
          </div>
          <div className="flex space-x-2">
            <Button data-testid="charts-notice-idea-load-btn">Load</Button>
            <Button
              data-testid="charts-notice-idea-delete-btn"
              disabled={isDeleting}
              theme="red"
              onClick={(ev: React.ChangeEvent<HTMLButtonElement>) => {
                ev.stopPropagation()
                onDeleteIdeaClick.current(idea)
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
    </>
  )
}

export function IdeasDialog(props: any) {
  return (
    <Dialog state={props.state} aria-label="Your ideas" data-testid="charts-ideas-modal" className="w-145">
      <DialogHeader onCloseClick={() => props.state.hide()}>Your ideas</DialogHeader>
      <DialogBody className="space-y-1">
        <IdeasList />
      </DialogBody>
      <DialogFooter>
        <Button onClick={() => props.state.hide()} data-testid="charts-ideas-modal-cancel">
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export async function saveIdea(layoutModel: ChartLayout) {
  const activeChart = layoutModel.activeChart
  const activeChartQuote = activeChart.quote().toJSON()
  const { ticker, instrument, cid } = activeChart
  const period = activeChartQuote.timeframe
  const { ChartSettings } = layoutModel.settings
  const layout: ObjectHash = layoutModel.toObject()
  layout.layout = LayoutType['1h1v']
  layout.barWidth = ChartSettings.center.barWidth
  layout.barMargin = ChartSettings.center.barMargin
  layout.charts = layout.charts
    .filter((c: ObjectHash) => c.chartId === cid)
    .map((c: ObjectHash) => ({ ...c, dateRange: null, refreshData: false }))

  delete layout.charts[0].chartId

  // TODO: replace with better solution after indicators overhaul
  const getChartTickers = (layoutModelObjectPanes: { elements: { type: string; tickers: string }[] }[]) => {
    let tickers = []
    let tickersString = ''
    for (const pane of layoutModelObjectPanes) {
      for (const element of pane.elements) {
        if (element.type === 'indicators/perf') {
          tickersString = element.tickers
        }
      }
    }
    tickers = tickersString.split(',').filter((item) => item)
    if (!tickers.includes(ticker)) {
      tickers.push(ticker.replace('@', ''))
    }
    return tickers
  }
  const tickers = getChartTickers(layout.charts[0].panes)
  const quotes = Quote.select(
    (quote: Quote) =>
      tickers.includes(quote.ticker) && quote.timeframe === activeChart.timeframe && layoutModel.isIdeaId(quote.ideaID)
  ).filter((element, index, array) => array.findIndex((t) => t.ticker === element.ticker) === index)
  const quotesJsonString = JSON.stringify(quotes.map((q) => omit(q.toJSON(), ['ideaID', 'chartUuid'])))

  const chart = JSON.stringify(layout)
  const note = layoutModel.idea?.note || ''
  window.gtag?.('event', 'save', { event_category: 'ideas' })

  if (tickers.length !== quotes.length) {
    captureException(new Error('Idea saved with inconsistent quotes data'), {
      extra: {
        tickers,
        quoteTickersTimeframes: quotes.map((quote) => ({ ticker: quote.ticker, timeframe: quote.timeframe })),
        chartLayout: layoutModel.toConfig(['colors', 'panes']),
      },
    })
  }

  const data = await fetchApi<{ id: string }>({
    location: '/api/idea_save.ashx',
    method: 'POST',
    body: getFormDataFromObject({ ticker, period, chart, quotes: quotesJsonString, note }),
  })

  const ideaUrl = getIdeaUrl({
    ticker,
    period,
    instrument,
    id: data.id,
  })

  if (process.env.IS_E2E_TESTING) {
    window.location.href = ideaUrl
  } else {
    // need to be in setTimeout because Safari block any call to window.open made in async,
    // this is hack to force run window.open in main thread
    setTimeout(() => {
      window.open(ideaUrl, 'FinvizSavedIdea')
    })
  }
}
